import type { NostrEvent } from '@nostrify/nostrify';

// Kind 34550: Community Definition (NIP-72)
export interface CommunityDefinition extends NostrEvent {
  kind: 34550;
}

// Extract community metadata from tags
export interface CommunityMetadata {
  id: string; // d tag value
  name: string;
  description?: string;
  image?: string;
  // a tags contain badge references with rank
  badges: BadgeReference[];
  // p tags contain founder pubkeys
  founders: string[];
  relays: string[];
}

// Kind 30009: Badge Definition (NIP-58)
export interface BadgeDefinition extends NostrEvent {
  kind: 30009;
}

// Extract badge metadata from tags
export interface BadgeMetadata {
  id: string; // d tag value
  name: string;
  description?: string;
  image?: string;
  thumb?: string;
}

// Kind 8: Badge Award (NIP-58)
export interface BadgeAward extends NostrEvent {
  kind: 8;
}

// Extract award data from tags
export interface AwardData {
  badgeRef: string; // The a tag reference (e.g., "30009:pubkey:badge-id")
  recipient: string; // The p tag value (recipient pubkey)
  relay?: string; // Optional relay hint
}

// Kind 5: Deletion (NIP-09)
export interface BadgeDeletion extends NostrEvent {
  kind: 5;
}

// Badge reference from community definition (a tag)
export interface BadgeReference {
  kind: number; // Always 30009
  pubkey: string; // Origin pubkey
  identifier: string; // Badge d tag
  relay?: string; // Relay hint
  rank: number; // Authority rank (0 = highest)
}

// Parsed a tag structure
export interface ParsedATag {
  kind: number;
  pubkey: string;
  identifier: string;
  relay?: string;
}

// Authority chain for a badge award
export interface AuthorityChain {
  recipient: string; // The user who received the badge
  badge: BadgeReference; // The badge they received
  granter: string; // Who granted this badge
  granterBadge?: BadgeReference; // The badge the granter holds (if not a founder)
  parentChain?: AuthorityChain; // Recursive chain to root
  isValid: boolean; // Whether the chain is currently valid
  invalidReason?: string; // Why the chain is invalid (if applicable)
}

// Badge holder information with authority status
export interface BadgeHolder {
  pubkey: string;
  badges: BadgeAward[];
  authorityChains: AuthorityChain[];
  isValid: boolean;
  highestRank: number; // Lower number = more authority
}

// Badge tier information
export interface BadgeTier {
  rank: number;
  badge: BadgeReference;
  definition?: BadgeMetadata;
  holders: BadgeHolder[];
}

// Community with all badge data
export interface CommunityData {
  definition: CommunityDefinition;
  metadata: CommunityMetadata;
  badgeDefinitions: Map<string, BadgeDefinition>;
  badgeTiers: BadgeTier[];
  totalHolders: number;
}

// Helper to parse an a tag
export function parseATag(aTag: string[]): ParsedATag | null {
  if (aTag.length < 2 || aTag[0] !== 'a') return null;
  
  const parts = aTag[1].split(':');
  if (parts.length < 3) return null;
  
  const kind = parseInt(parts[0], 10);
  if (isNaN(kind)) return null;
  
  return {
    kind,
    pubkey: parts[1],
    identifier: parts[2],
    relay: parts[3],
  };
}

// Helper to get tag value by name
export function getTagValue(event: NostrEvent, tagName: string): string | undefined {
  const tag = event.tags.find(([name]) => name === tagName);
  return tag?.[1];
}

// Helper to get all tag values by name
export function getTagValues(event: NostrEvent, tagName: string): string[] {
  return event.tags
    .filter(([name]) => name === tagName)
    .map(([, value]) => value);
}

// Parse community definition into metadata
export function parseCommunityMetadata(event: CommunityDefinition): CommunityMetadata {
  const badges: BadgeReference[] = [];
  
  for (const tag of event.tags) {
    if (tag[0] === 'a') {
      const parsed = parseATag(tag);
      if (parsed && parsed.kind === 30009) {
        badges.push({
          kind: parsed.kind,
          pubkey: parsed.pubkey,
          identifier: parsed.identifier,
          relay: parsed.relay,
          rank: parseInt(tag[tag.length - 1], 10) || 0, // Last element is rank
        });
      }
    }
  }
  
  // Sort badges by rank
  badges.sort((a, b) => a.rank - b.rank);
  
  return {
    id: getTagValue(event, 'd') || '',
    name: getTagValue(event, 'name') || 'Unnamed Community',
    description: getTagValue(event, 'description'),
    image: getTagValue(event, 'image'),
    badges,
    founders: getTagValues(event, 'p'),
    relays: getTagValues(event, 'relay'),
  };
}

// Parse badge definition into metadata
export function parseBadgeMetadata(event: BadgeDefinition): BadgeMetadata {
  return {
    id: getTagValue(event, 'd') || '',
    name: getTagValue(event, 'name') || 'Unnamed Badge',
    description: getTagValue(event, 'description'),
    image: getTagValue(event, 'image'),
    thumb: getTagValue(event, 'thumb'),
  };
}

// Parse badge award into data
export function parseBadgeAward(event: BadgeAward): AwardData | null {
  const aTag = event.tags.find(([name]) => name === 'a');
  const pTag = event.tags.find(([name]) => name === 'p');
  
  if (!aTag || !pTag) return null;
  
  return {
    badgeRef: aTag[1],
    recipient: pTag[1],
    relay: pTag[2],
  };
}

// Build an a tag string
export function buildATag(ref: BadgeReference): string {
  let tag = `${ref.kind}:${ref.pubkey}:${ref.identifier}`;
  if (ref.relay) tag += `:${ref.relay}`;
  return tag;
}

// Check if a pubkey is a founder of a community
export function isFounder(pubkey: string, community: CommunityMetadata): boolean {
  return community.founders.includes(pubkey);
}

// Check if a granter can award a badge (granter rank < awarded rank)
export function canAwardBadge(
  granterRank: number | undefined,
  awardedRank: number
): boolean {
  // Founders have rank 0, undefined means not a badge holder
  if (granterRank === undefined) return false;
  return granterRank < awardedRank;
}
